import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToolCost, type ToolCostFilters } from "@/hooks/useToolCost";
import { Loader2, Wrench } from "lucide-react";

interface ToolCostSectionProps {
  projectSlug: string | null;
  filters?: ToolCostFilters;
}

function formatAmount(amount: number, unit: string): string {
  if (unit === "USD") return `$${amount.toFixed(2)}`;
  if (unit === "tokens") {
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
    return amount.toFixed(0);
  }
  return amount.toFixed(2);
}

function formatCostUsd(cost: number | null): string {
  if (cost === null) return "--";
  return `$${cost.toFixed(2)}`;
}

export function ToolCostSection({ projectSlug, filters = {} }: ToolCostSectionProps) {
  const { items, loading, error } = useToolCost(projectSlug, filters);

  if (loading && items.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-lg border text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading tool cost data...
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center">
        <Wrench className="h-8 w-8 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          Tool cost data is not available yet.
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center">
        <Wrench className="h-8 w-8 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No tool cost data yet.</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Tool Cost</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 font-medium">Tool</th>
                <th className="pb-2 font-medium">Unit</th>
                <th className="pb-2 font-medium text-right">Amount</th>
                <th className="pb-2 font-medium text-right">Cost (USD)</th>
                <th className="pb-2 font-medium text-right">Events</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={`${item.tool_name}-${item.unit}`}
                  className="border-b last:border-0"
                >
                  <td className="py-2 font-medium">{item.tool_name}</td>
                  <td className="py-2">{item.unit}</td>
                  <td className="py-2 text-right tabular-nums">
                    {formatAmount(item.amount, item.unit)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatCostUsd(item.cost_usd)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {item.event_count}
                  </td>
                </tr>
              ))}
            </tbody>
            {items.some((i) => i.cost_usd !== null) && (
              <tfoot>
                <tr className="border-t font-semibold">
                  <td className="pt-2" colSpan={3}>
                    Total (USD)
                  </td>
                  <td className="pt-2 text-right tabular-nums">
                    {formatCostUsd(
                      items.reduce(
                        (sum, i) => sum + (i.cost_usd ?? 0),
                        0,
                      ),
                    )}
                  </td>
                  <td className="pt-2 text-right tabular-nums">
                    {items.reduce((sum, i) => sum + i.event_count, 0)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
